import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Clock, Plus, Edit2, Trash2, Save, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ShiftSettings() {
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  
  // Form states
  const [formData, setFormData] = useState({
    name: '',
    start_time: '',
    end_time: '',
    description: ''
  });

  useEffect(() => {
    fetchShifts();
  }, []);

  const fetchShifts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/shifts/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setShifts(response.data.shifts || []);
      }
    } catch (error) {
      console.error('Error fetching shifts:', error);
      // If no settings yet, use default shifts
      setShifts([
        { id: '1', name: 'Shift Pagi', start_time: '08:00', end_time: '16:00', description: 'Pagi - Siang', is_default: true },
        { id: '2', name: 'Shift Siang', start_time: '16:00', end_time: '00:00', description: 'Siang - Malam', is_default: true },
        { id: '3', name: 'Shift Malam', start_time: '00:00', end_time: '08:00', description: 'Malam - Pagi', is_default: true }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setShowAddForm(true);
    setFormData({ name: '', start_time: '', end_time: '', description: '' });
  };

  const handleEdit = (shift) => {
    setEditingId(shift.id);
    setFormData({
      name: shift.name,
      start_time: shift.start_time,
      end_time: shift.end_time,
      description: shift.description || ''
    });
  };

  const handleCancel = () => {
    setEditingId(null);
    setShowAddForm(false);
    setFormData({ name: '', start_time: '', end_time: '', description: '' });
  };

  const handleSave = async (shiftId = null) => {
    if (!formData.name || !formData.start_time || !formData.end_time) {
      toast.error('Nama shift dan jam harus diisi');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      if (shiftId) {
        // Update existing
        await axios.put(
          `${API_URL}/api/shifts/settings/${shiftId}`,
          formData,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        toast.success('Shift berhasil diupdate');
      } else {
        // Create new
        await axios.post(
          `${API_URL}/api/shifts/settings`,
          formData,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        toast.success('Shift berhasil ditambahkan');
      }
      
      handleCancel();
      fetchShifts();
    } catch (error) {
      console.error('Error saving shift:', error);
      toast.error(error.response?.data?.detail || 'Gagal menyimpan shift');
    }
  };

  const handleDelete = async (shiftId) => {
    toast.warning('Yakin ingin menghapus shift ini?', {
      description: 'Shift yang dihapus tidak dapat dikembalikan',
      duration: 5000,
      action: {
        label: 'Hapus',
        onClick: async () => {
          try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API_URL}/api/shifts/settings/${shiftId}`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            
            toast.success('Shift berhasil dihapus');
            fetchShifts();
          } catch (error) {
            console.error('Error deleting shift:', error);
            toast.error('Gagal menghapus shift');
          }
        }
      }
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Clock className="h-6 w-6 text-blue-600" />
            Pengaturan Jam Shift
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Atur jam kerja untuk setiap shift kasir
          </p>
        </div>
        <button
          onClick={handleAdd}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Tambah Shift
        </button>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <div className="bg-white border-2 border-blue-500 rounded-lg shadow-lg p-6 animate-in fade-in slide-in-from-top-2 duration-300">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Tambah Shift Baru</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nama Shift *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Contoh: Shift Pagi"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Deskripsi (opsional)
              </label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Contoh: Pagi - Siang"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Jam Mulai *
              </label>
              <input
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Jam Selesai *
              </label>
              <input
                type="time"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="flex gap-2 mt-4">
            <button
              onClick={() => handleSave()}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center gap-2"
            >
              <Save className="h-4 w-4" />
              Simpan
            </button>
            <button
              onClick={handleCancel}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition flex items-center gap-2"
            >
              <X className="h-4 w-4" />
              Batal
            </button>
          </div>
        </div>
      )}

      {/* Shifts List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {shifts.map((shift) => (
          <div
            key={shift.id}
            className={`bg-white border-2 rounded-lg shadow-sm p-5 ${
              editingId === shift.id ? 'border-blue-500' : 'border-gray-200'
            }`}
          >
            {editingId === shift.id ? (
              // Edit Mode
              <div className="space-y-3">
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Deskripsi"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <div className="flex gap-2">
                  <input
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <span className="flex items-center text-gray-500">-</span>
                  <input
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleSave(shift.id)}
                    className="flex-1 px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition flex items-center justify-center gap-1"
                  >
                    <Save className="h-3 w-3" />
                    Simpan
                  </button>
                  <button
                    onClick={handleCancel}
                    className="flex-1 px-3 py-2 bg-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-300 transition"
                  >
                    Batal
                  </button>
                </div>
              </div>
            ) : (
              // View Mode
              <>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="font-bold text-gray-900 text-lg">{shift.name}</h3>
                    {shift.description && (
                      <p className="text-xs text-gray-500 mt-0.5">{shift.description}</p>
                    )}
                  </div>
                  {shift.is_default && (
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-semibold rounded-full">
                      Default
                    </span>
                  )}
                </div>

                <div className="bg-gray-50 rounded-lg p-3 mb-3">
                  <div className="flex items-center justify-center gap-2 text-gray-700">
                    <Clock className="h-4 w-4" />
                    <span className="font-bold text-lg">{shift.start_time}</span>
                    <span className="text-gray-400">-</span>
                    <span className="font-bold text-lg">{shift.end_time}</span>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(shift)}
                    className="flex-1 px-3 py-2 bg-blue-50 text-blue-600 text-sm rounded-lg hover:bg-blue-100 transition flex items-center justify-center gap-1"
                  >
                    <Edit2 className="h-3 w-3" />
                    Edit
                  </button>
                  {!shift.is_default && (
                    <button
                      onClick={() => handleDelete(shift.id)}
                      className="flex-1 px-3 py-2 bg-red-50 text-red-600 text-sm rounded-lg hover:bg-red-100 transition flex items-center justify-center gap-1"
                    >
                      <Trash2 className="h-3 w-3" />
                      Hapus
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        ))}
      </div>

      {shifts.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Clock className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Belum ada shift yang dikonfigurasi</p>
          <button
            onClick={handleAdd}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            Tambah Shift Pertama
          </button>
        </div>
      )}

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800">
           <strong>Tips:</strong> Shift digunakan untuk tracking dan analisa kinerja per periode waktu. 
          Kasir akan otomatis masuk ke shift yang sesuai berdasarkan jam login.
        </p>
      </div>
    </div>
  );
}
